import React, {useState, useEffect} from 'react';
import {Card, Radio, Button, message, Spin, Tag, Empty} from 'antd';
import {SlidersFilled, PlusCircleOutlined, OrderedListOutlined} from '@ant-design/icons';
import {motion, AnimatePresence} from 'framer-motion';
import {getJobPreferencesApi, updateJobPreferencesApi} from '../../api/job-preferences';
import {TouchAppOutlined} from '@mui/icons-material';
import {useAtom} from 'jotai';
import {jobPreferenceChangedAtom} from '../../utils/atoms';
import {refreshUserDataComplete} from '../../utils/authHelpers';
import {useAuthRedirect} from '../AuthComponents/redirector';

const JobPreferenceSelector = ({notRedirect = false}) => {
	const [categories, setCategories] = useState([]);
	const [selectedCategories, setSelectedCategories] = useState({});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [, setJobPreferenceChanged] = useAtom(jobPreferenceChangedAtom);
	const {handleJobPreferenceRedirect} = useAuthRedirect();
	// Colors for different priority levels
	const priorityColors = {
		1: 'bg-lightBlue border-lightBlue',
		2: 'bg-permBlue border-permBlue text-white',
		3: 'bg-antBlue border-antBlue text-white',
		4: 'bg-darkBlue border-darkBlue text-white',
		5: 'bg-deepBlue border-deepBlue text-white',
	};

	useEffect(() => {
		fetchPreferences();
	}, []);

	const fetchPreferences = async () => {
		setLoading(true);
		try {
			const response = await getJobPreferencesApi();
			setCategories(response.data.available_categories || []);
			setSelectedCategories(response.data.preferred_categories || {});
			setLoading(false);
		} catch (error) {
			console.error('Error fetching preferences:', error);
			message.error('Failed to load job preferences');
			setLoading(false);
		}
	};

	const toggleCategory = (category) => {
		if (selectedCategories[category]) {
			const newSelectedCategories = {...selectedCategories};
			delete newSelectedCategories[category];
			setSelectedCategories(newSelectedCategories);
		} else {
			if (Object.keys(selectedCategories).length >= 5) {
				message.warning('You can only select up to 5 categories');
				return;
			}
			setSelectedCategories({
				...selectedCategories,
				[category]: 3,
			});
		}
	};

	const changePriority = (category, priority) => {
		setSelectedCategories({
			...selectedCategories,
			[category]: priority,
		});
	};

	const savePreferences = async () => {
		setSaving(true);
		try {
			await updateJobPreferencesApi(selectedCategories);
			await refreshUserDataComplete();
			message.success('Job preferences saved successfully');
			setJobPreferenceChanged(true);
			handleJobPreferenceRedirect(notRedirect);
		} catch (error) {
			console.error('Error saving preferences:', error);
			message.error('Failed to save preferences');
		} finally {
			setSaving(false);
		}
	};

	const renderPriorityRadio = (category) => (
		<Radio.Group value={selectedCategories[category]} onChange={(e) => changePriority(category, e.target.value)} className="flex justify-between grow max-w-[340px]" buttonStyle="solid">
			{[1, 2, 3, 4, 5].map((value) => (
				<Radio.Button key={value} value={value} className="text-center flex-1">
					{value}
				</Radio.Button>
			))}
		</Radio.Group>
	);

	if (loading) {
		return (
			<div className="flex justify-center items-center h-64">
				<Spin size="large" />
			</div>
		);
	}

	return (
		<Card className="w-full max-w-6xl mx-auto border-none shadow-none" size="small">
			<h2 className="text-3xl font-normal mb-2 flex items-center text-darkCharcoal text-center mx-auto w-fit">Personalize your job recommendations</h2>
			<p className="mb-10 text-charcoal text-base font-normal text-center">
				Select job categories aligned with your professional interests. We'll prioritize opportunities that match your preferences for a more tailored experience.
			</p>
			<div className="mb-10">
				<h3 className="text-base font-normal mb-3 text-center flex items-center justify-center gap-2 text-antBlue">
					<TouchAppOutlined className="text-base" /> Select up to 5 categories that match your career goals
				</h3>
				<div className="flex flex-wrap gap-2 justify-center items-center">
					{categories
						.filter((cat) => !selectedCategories[cat])
						.map((category) => (
							<Tag
								key={category}
								className={`px-3 flex items-center gap-2 m-0 py-2 border rounded-full cursor-pointer transition-colors ${
									Object.keys(selectedCategories).length >= 5
										? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
										: 'border-lightBlue bg-backgroundGreyBlue text-darkCharcoal hover:bg-lightBlue'
								}`}
								onClick={() => Object.keys(selectedCategories).length < 5 && toggleCategory(category)}>
								{category}
								<PlusCircleOutlined />
							</Tag>
						))}
				</div>
			</div>
			<div className="mb-4">
				<h3 className="text-base font-normal mb-2 flex items-center text-center mx-auto w-fit text-antBlue">
					<OrderedListOutlined className="mr-2" /> Prioritize your selected categories {Object.keys(selectedCategories).length > 0 && `(${Object.keys(selectedCategories).length})`}
				</h3>
				<div className="flex flex-col-reverse md:flex-row gap-4 p-3 bg-white rounded-lg border border-gray-200/50">
					<div className="flex-grow ">
						{Object.keys(selectedCategories).length === 0 ? (
							<Empty description="Please select job categories that align with your career interests (up to 5)" className="my-auto mt-4" />
						) : (
							<div className="space-y-2">
								<AnimatePresence>
									{Object.entries(selectedCategories)
										.sort(([, a], [, b]) => b - a) // Sort by priority (highest first)
										.map(([category, priority], index) => (
											<motion.div
												key={category}
												layout
												initial={{opacity: 0, y: 20}}
												animate={{opacity: 1, y: 0}}
												exit={{opacity: 0, scale: 0.8}}
												transition={{
													type: 'spring',
													stiffness: 350,
													damping: 25,
													duration: 0.3,
												}}
												className="border rounded-lg p-2 bg-white">
												<div className="flex justify-between items-center gap-2 flex-wrap">
													<div className="flex items-center gap-2">
														<Tag className={`${priorityColors[priority]} px-1 py-0 text-xs border rounded-md font-medium mr-1`}>{priority}</Tag>
														<Tag className="font-medium text-xs rounded-full px-2 py-0">{category}</Tag>
													</div>
													<div className="flex items-center gap-2 flex-wrap">
														{renderPriorityRadio(category)}
														<Button size="small" type="text" danger onClick={() => toggleCategory(category)}>
															Remove
														</Button>
													</div>
												</div>
											</motion.div>
										))}
								</AnimatePresence>
							</div>
						)}
					</div>

					<div className="md:w-48 flex-shrink-0 p-3 bg-white rounded-lg border border-gray-200/50 h-fit">
						<h4 className="text-sm font-medium mb-2 text-gray-700 flex items-center">
							<SlidersFilled className="mr-2" /> Priority Scale
						</h4>
						<div className="text-xs text-gray-600 space-y-2">
							<div className="flex items-center">
								<Tag className={`${priorityColors[5]} px-1 py-0 text-xs border rounded-md font-medium mr-2`}>5</Tag>
								<span>Highest priority</span>
							</div>
							<div className="flex items-center">
								<Tag className={`${priorityColors[3]} px-1 py-0 text-xs border rounded-md font-medium mr-2`}>3</Tag>
								<span>Medium priority</span>
							</div>
							<div className="flex items-center">
								<Tag className={`${priorityColors[1]} px-1 py-0 text-xs border rounded-md font-medium mr-2`}>1</Tag>
								<span>Lower priority</span>
							</div>
						</div>
						<p className="mt-2 text-xs text-gray-500">Select a priority level for each job category.</p>
					</div>
				</div>
			</div>

			<div className="flex justify-end">
				<Button type="primary" onClick={savePreferences} loading={saving} size="large" className="w-full">
					Save Preferences
				</Button>
			</div>
		</Card>
	);
};

export default JobPreferenceSelector;
